import _ from 'lodash';
import { useRouter } from '@uirouter/react';

import {
  SSHCredentialFormValues,
  SSHCredentials,
  Credential,
  CreateCredentialPayload,
  GenericFormValues,
  UpdateCredentialPayload,
  CredentialType,
  OmniFormValues,
  GitCredentialFormValues,
} from '../types';
import { SSHCredentialsForm } from '../components/SSHCredentialsForm/SSHCredentialsForm';
import { getUnchangedSensitiveFields, trimObject } from '../utils';
import {
  useCloudCredentials,
  useUpdateCredentialMutation,
} from '../cloudSettings.service';
import { OmniCredentialsForm } from '../components/OmniCredentialsForm';
import { GitCredentialsForm } from '../components/GitCredentialsForm/GitCredentialsForm';

type Props = {
  credential: Credential;
};

enum FormTypes {
  OMNI = 'omni',
  GIT = 'git',
  USERNAME_PASSWORD = 'username-password',
}

export function EditCredentialForm({ credential }: Props) {
  const { provider: credentialType } = credential;
  const formType = getFormType(credentialType);

  const router = useRouter();
  const cloudCredentialsQuery = useCloudCredentials();
  const credentialNames =
    cloudCredentialsQuery.data
      ?.filter(
        (c) => c.name !== credential.name && c.provider === credentialType
      )
      .map((c: CreateCredentialPayload) => c.name) || [];

  const updateCredentialMutation = useUpdateCredentialMutation(
    credential.id,
    formType === FormTypes.GIT
  );

  const initialValues = {
    name: credential.name,
    provider: credential.provider,
    credentials: credential.credentials,
  };

  return (
    <>
      {formType === FormTypes.OMNI && 'endpoint' in credential.credentials && (
        <OmniCredentialsForm
          selectedProvider={credentialType}
          isEditing
          isLoading={updateCredentialMutation.isLoading}
          onSubmit={onSubmit}
          credentialNames={credentialNames}
          initialValues={
            {
              ...initialValues,
              credentials: {
                ...initialValues.credentials,
                serviceAccountKey: '',
              },
            } as OmniFormValues
          }
          placeholderText="*******"
        />
      )}
      {formType === FormTypes.GIT && 'username' in credential.credentials && (
        <GitCredentialsForm
          selectedProvider={credentialType}
          isEditing
          isLoading={updateCredentialMutation.isLoading}
          onSubmit={onSubmit}
          credentialNames={credentialNames}
          initialValues={
            {
              ...initialValues,
              credentials: {
                ...initialValues.credentials,
                personalAccessToken: '',
              },
            } as GitCredentialFormValues
          }
        />
      )}
      {formType === FormTypes.USERNAME_PASSWORD &&
        'username' in credential.credentials && (
          <SSHCredentialsForm
            selectedProvider={credentialType}
            isEditing
            isLoading={updateCredentialMutation.isLoading}
            onSubmit={onSubmit}
            credentialNames={credentialNames}
            hasSSHKey={!!(credential.credentials as SSHCredentials).privateKey}
            hasPassphrase={
              !!(credential.credentials as SSHCredentials).passphrase
            }
            initialValues={
              {
                ...initialValues,
                credentials: {
                  ...initialValues.credentials,
                  password: '',
                  privateKey: '',
                  passphrase: '',
                },
              } as SSHCredentialFormValues
            }
          />
        )}
    </>
  );

  function onSubmit(values: GenericFormValues) {
    const newCredentials: UpdateCredentialPayload = {
      name: values.name.trim(),
      provider: credentialType,
    };

    if ('credentials' in values) {
      const unchangedSensitiveFields = getUnchangedSensitiveFields(
        values.credentials
      );
      // keep the unchanged sensitive fields out of put request payload
      const visibleCredentials = _.omit(
        values.credentials,
        unchangedSensitiveFields
      );
      if (Object.keys(visibleCredentials).length > 0) {
        newCredentials.credentials = trimObject(visibleCredentials);
      }
      // base64 encode the private key
      if (
        newCredentials.credentials &&
        'privateKey' in newCredentials.credentials &&
        typeof newCredentials.credentials.privateKey === 'string'
      ) {
        newCredentials.credentials.privateKey = window.btoa(
          newCredentials.credentials.privateKey
        );
      }
    }

    updateCredentialMutation.mutate({
      credential: newCredentials,
    });
    router.stateService.go('portainer.settings.sharedcredentials');
  }
}

function getFormType(credentialType: CredentialType) {
  if (credentialType === CredentialType.OMNI) {
    return FormTypes.OMNI;
  }
  if (credentialType === CredentialType.GIT) {
    return FormTypes.GIT;
  }
  return FormTypes.USERNAME_PASSWORD;
}
